import api, { apiClient } from './api';

/** REV-476: issues-breakdown export formats. REV-816 adds CSV. */
export type IssuesBreakdownExportFormat = 'html' | 'docx' | 'csv';

/** REV-71: executive-summary export formats (same as issues-breakdown). */
export type ExecutiveSummaryExportFormat = 'html' | 'docx';

export type Severity = 'critical' | 'major' | 'minor' | 'info';

export interface IssuesBreakdownReport {
  context: { id: string; name: string; canonical_identifier: string };
  totals: { open: number; all: number };
  by_severity: Record<Severity, number>;
  by_status: Record<string, number>;
  by_wcag_level: { A: number; AA: number; AAA: number; unspecified: number };
  top_rules: Array<{
    rule_id: string | null;
    name: string | null;
    severity: string | null;
    count: number;
  }>;
  top_pages: Array<{
    target_id: string | null;
    url: string | null;
    title: string | null;
    count: number;
  }>;
}

/** REV-457: org-level dashboard summary powering the homepage charts. */
export interface OrgDashboardContextBreakdown {
  context_id: string | null;
  /** REV-568: present so the all-orgs view can link a row to its own org. */
  organization_id?: string | null;
  name: string | null;
  canonical_identifier: string | null;
  open: number;
  by_severity: Record<Severity, number>;
}

export interface OrganizationDashboard {
  totals: { open: number; all: number; contexts: number };
  by_severity: Record<Severity, number>;
  by_status: Record<string, number>;
  by_wcag_level: { A: number; AA: number; AAA: number; unspecified: number };
  top_rules: Array<{
    rule_id: string | null;
    name: string | null;
    severity: string | null;
    count: number;
  }>;
  /**
   * REV-483: open findings by afixt-tests content category, biggest first.
   * Rules the catalog does not know (findings ingested from external tools)
   * appear as the literal category 'Uncategorized' rather than being dropped,
   * so these counts sum to totals.open.
   */
  by_content_category: Array<{ category: string; count: number }>;
  /**
   * REV-483: open findings by the engine's calculated priority. 5 is the most
   * urgent, 1 the least; every level is present even at zero. `unknown` holds
   * findings whose rule is outside the catalog.
   */
  by_priority: Record<'1' | '2' | '3' | '4' | '5' | 'unknown', number>;
  by_context: OrgDashboardContextBreakdown[];
}

/** REV-9: findings open-count trend over time. */
export type TrendBucket = 'day' | 'week';

export interface FindingsTrendPoint {
  /** ISO date (YYYY-MM-DD) of the bucket end — the "as-of" date. */
  date: string;
  /** Findings open as of the bucket end. */
  open: number;
  /** Findings first detected within the bucket. */
  new: number;
  /** Findings closed within the bucket. */
  closed: number;
}

export interface FindingsTrend {
  context_id: string | null;
  bucket: TrendBucket;
  from: string;
  to: string;
  series: FindingsTrendPoint[];
  totals: { current_open: number; opened_in_range: number; closed_in_range: number };
}

export interface FindingsTrendParams {
  contextId?: string;
  from?: string;
  to?: string;
  bucket?: TrendBucket;
  severity?: Severity;
}

/** REV-71: Context-level executive summary — concise stakeholder rollup. */
export type TrendDirection = 'up' | 'down' | 'flat';

export interface ExecutiveSummary {
  organization: { id: string; name: string | null };
  context: { id: string; name: string; canonical_identifier: string };
  posture: {
    total_open: number;
    total_all: number;
    by_severity: Record<Severity, number>;
    by_wcag_level: { A: number; AA: number; AAA: number; unspecified: number };
  };
  top_risks: {
    rules: Array<{
      rule_id: string | null;
      name: string | null;
      severity: string | null;
      count: number;
    }>;
    pages: Array<{
      target_id: string | null;
      url: string | null;
      title: string | null;
      count: number;
    }>;
  };
  trend: {
    direction: TrendDirection;
    net_change: number;
    current_open: number;
    baseline_open: number;
    window_days: number;
    opened_in_range: number;
    closed_in_range: number;
    from: string;
    to: string;
  };
}

/**
 * REV-588: fill in any section the response omits.
 *
 * Every consumer reads straight into these objects — `data.totals.open`,
 * `data.by_wcag_level.A`, `data.by_severity[s]`. TypeScript says they are all
 * present, so nothing guards them; a body that omits one therefore throws
 * during render and (before the error boundary) blanked the document. The
 * landing page hit this, which is the worst place for it because it is where
 * the boundary's "Go to home" sends people.
 *
 * Normalising once here beats optional-chaining every read site: there is one
 * place to be right, and it stays right as new consumers are added. A missing
 * section legitimately means zero, so an empty shape is the honest default —
 * this is not papering over an error, which is still surfaced by the request
 * failing.
 */
function normalizeDashboard<T extends OrganizationDashboard>(data: T): T {
  return {
    ...data,
    // The `Partial` casts are the point: the declared type promises these keys,
    // which is exactly the lie that causes the crash. Without the cast TS says
    // the defaults are unreachable and refuses the spread.
    totals: {
      open: 0,
      all: 0,
      contexts: 0,
      ...((data?.totals ?? {}) as Partial<OrganizationDashboard['totals']>),
    },
    by_severity: { ...(data?.by_severity ?? {}) } as OrganizationDashboard['by_severity'],
    by_status: { ...(data?.by_status ?? {}) },
    by_wcag_level: {
      A: 0,
      AA: 0,
      AAA: 0,
      unspecified: 0,
      ...((data?.by_wcag_level ?? {}) as Partial<OrganizationDashboard['by_wcag_level']>),
    },
    top_rules: data?.top_rules ?? [],
    by_content_category: data?.by_content_category ?? [],
    by_priority: data?.by_priority ?? ({} as OrganizationDashboard['by_priority']),
    by_context: data?.by_context ?? [],
  };
}

/**
 * REV-215 / REV-896: an organization's status report over a window: findings,
 * remediation progress, activity, top tracked places and recommendations.
 * Served by GET /v1/organizations/:orgId/reports/status to any member.
 */
export type StatusReportFindingSeverity = 'critical' | 'major' | 'minor' | 'info';
export type StatusReportPlanStatus = 'draft' | 'active' | 'completed' | 'archived';
export type StatusReportItemStatus = 'open' | 'in_progress' | 'done' | 'wont_fix';

export interface StatusReport {
  organization: {
    id: string;
    name: string;
    slug: string;
    contexts_count: number;
  };
  window: {
    from: string;
    to: string;
  };
  findings: {
    new_in_window: number;
    /**
     * REV-939: the window contains the organization's first completed scan
     * (at least one completed run in it and none before), so new_in_window is
     * the baseline it started from, not newly introduced issues. False for a
     * window with no scans in or before it.
     */
    baseline: boolean;
    /**
     * REV-939: of new_in_window, the findings on places whose first completed
     * scan is in this window — their baseline — and how many such places.
     * The API leaves these out of its capacity comparison.
     */
    baseline_new_in_window: number;
    baseline_contexts_in_window: number;
    resolved_in_window: number;
    open_total: number;
    open_by_severity: Record<StatusReportFindingSeverity, number>;
  };
  remediation: {
    plans_total: number;
    plans_by_status: Record<StatusReportPlanStatus, number>;
    plans_completed_in_window: number;
    items_total: number;
    items_by_status: Record<StatusReportItemStatus, number>;
    items_done_in_window: number;
  };
  activity: {
    advisory_notes_in_window: number;
  };
  top_contexts: Array<{
    context_id: string;
    name: string | null;
    canonical_identifier: string | null;
    open_count: number;
  }>;
  recommendations: Array<{
    code: string;
    severity: 'high' | 'medium' | 'low';
    message: string;
  }>;
}

export interface StatusReportRange {
  from?: string;
  to?: string;
}

export const reportingService = {
  /**
   * GET /v1/organizations/:orgId/reports/status (REV-896)
   */
  async getStatusReport(orgId: string, range: StatusReportRange = {}): Promise<StatusReport> {
    const params: Record<string, string> = {};
    if (range.from) params.from = range.from;
    if (range.to) params.to = range.to;
    const res = await apiClient.get<{ success: boolean; data: StatusReport }>(
      `/v1/organizations/${orgId}/reports/status`,
      params
    );
    return res.data;
  },

  /**
   * GET /v1/organizations/:orgId/contexts/:contextId/reports/issues-breakdown (REV-68)
   */
  async getIssuesBreakdown(
    orgId: string,
    contextId: string,
    topLimit?: number
  ): Promise<IssuesBreakdownReport> {
    const res = await apiClient.get<{ success: boolean; data: IssuesBreakdownReport }>(
      `/v1/organizations/${orgId}/contexts/${contextId}/reports/issues-breakdown`,
      topLimit ? { topLimit } : undefined
    );
    return res.data;
  },

  /**
   * GET /v1/organizations/:orgId/findings/trend (REV-9)
   *
   * Findings open-count trend over time. Optionally scoped to a Context and/or a
   * single severity; from/to are ISO dates and default (server-side) to the last
   * 90 days; bucket defaults to 'week'.
   */
  async getFindingsTrend(orgId: string, params: FindingsTrendParams = {}): Promise<FindingsTrend> {
    const query: Record<string, unknown> = {};
    if (params.contextId) query.context_id = params.contextId;
    if (params.from) query.from = params.from;
    if (params.to) query.to = params.to;
    if (params.bucket) query.bucket = params.bucket;
    if (params.severity) query.severity = params.severity;
    const res = await apiClient.get<{ success: boolean; data: FindingsTrend }>(
      `/v1/organizations/${orgId}/findings/trend`,
      Object.keys(query).length ? query : undefined
    );
    return res.data;
  },

  /**
   * GET /v1/organizations/:orgId/reports/dashboard (REV-457)
   */
  /**
   * REV-568: the rollup across every organization the caller belongs to.
   * Takes no organization argument on purpose — the API derives the scope from
   * the caller's own memberships, so there is nothing here that could ask for
   * someone else's data.
   */

  async getOrganizationDashboard(orgId: string, topLimit?: number): Promise<OrganizationDashboard> {
    const res = await apiClient.get<{ success: boolean; data: OrganizationDashboard }>(
      `/v1/organizations/${orgId}/reports/dashboard`,
      topLimit ? { topLimit } : undefined
    );
    return normalizeDashboard(res.data);
  },

  /**
   * GET /v1/organizations/:orgId/contexts/:contextId/reports/issues-breakdown/export (REV-476)
   *
   * Fetches the report export as a Blob. Uses the raw axios instance with
   * responseType: 'blob' so the request still carries the Bearer token via the
   * request interceptor — a plain new-tab navigation to the URL would not, and
   * the endpoint requires authentication. The caller is responsible for
   * triggering the download (docx) or opening the blob (html).
   */
  async getIssuesBreakdownExport(
    orgId: string,
    contextId: string,
    format: IssuesBreakdownExportFormat
  ): Promise<Blob> {
    const res = await api.get<Blob>(
      `/v1/organizations/${orgId}/contexts/${contextId}/reports/issues-breakdown/export`,
      { params: { format }, responseType: 'blob' }
    );
    return res.data;
  },

  /**
   * GET /v1/organizations/:orgId/contexts/:contextId/reports/executive-summary (REV-71)
   *
   * Concise, stakeholder-facing rollup: posture, top risks, and a 30-day trend.
   */
  async getExecutiveSummary(orgId: string, contextId: string): Promise<ExecutiveSummary> {
    const res = await apiClient.get<{ success: boolean; data: ExecutiveSummary }>(
      `/v1/organizations/${orgId}/contexts/${contextId}/reports/executive-summary`
    );
    return res.data;
  },

  /**
   * GET /v1/organizations/:orgId/contexts/:contextId/reports/executive-summary/export (REV-71)
   *
   * Fetches the executive-summary export as a Blob via the Bearer-authenticated
   * axios instance (a plain new-tab navigation would omit the token). Mirrors
   * getIssuesBreakdownExport: the caller triggers the download (docx) or opens
   * the blob (html).
   */
  async getExecutiveSummaryExport(
    orgId: string,
    contextId: string,
    format: ExecutiveSummaryExportFormat
  ): Promise<Blob> {
    const res = await api.get<Blob>(
      `/v1/organizations/${orgId}/contexts/${contextId}/reports/executive-summary/export`,
      { params: { format }, responseType: 'blob' }
    );
    return res.data;
  },
};
